import { useState } from "react";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useProjectsAdmin } from "../../hooks/useProjects";
import { AdminField, inputCls } from "./AdminLayout";
import { apiErrorMessage } from "../../lib/apiErrors";

const CATS = ["Apartment", "Villa", "Residential", "Modular Kitchen"];
const SPANS = [
  { value: "lg:col-span-4", label: "Small (4 cols)" },
  { value: "lg:col-span-8", label: "Wide (8 cols)" },
];

const EMPTY = {
  title: "",
  cat: "Apartment",
  area: "",
  image_url: "",
  span: "lg:col-span-4",
  sort_order: 0,
  published: true,
};

export default function AdminProjects() {
  const [reloadKey, setReloadKey] = useState(0);
  const { projects, loading, error: projectsError } = useProjectsAdmin(reloadKey);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = () => setReloadKey((k) => k + 1);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...EMPTY, sort_order: projects.length });
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title,
      cat: p.cat,
      area: p.area || "",
      image_url: p.image_url || p.img || "",
      span: p.span || "lg:col-span-4",
      sort_order: p.sort_order ?? 0,
      published: p.published !== false,
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.image_url) {
      toast.error("Title and image URL are required.");
      return;
    }
    const payload = {
      ...form,
      area: form.area?.trim() || null,
      sort_order: Number(form.sort_order) || 0,
    };
    setSaving(true);
    try {
      if (editingId) await api.put(`/projects/${editingId}`, payload);
      else await api.post("/projects", payload);
      toast.success(editingId ? "Project updated" : "Project created");
      setShowForm(false);
      refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Deleted");
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif-display text-4xl">Projects</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Shown on Home and Projects pages. Lower sort order appears first.
          </p>
        </div>
        <button
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <Plus size={18} /> New Project
        </button>
      </div>

      {projectsError === "projects_api_missing" && (
        <p className="mb-4 text-sm text-amber-700 dark:text-amber-400 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
          Projects API not available — restart the backend with the latest <code className="text-xs">server.py</code>.
        </p>
      )}
      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <div className="rounded-2xl border border-border overflow-hidden">
          {projects.length === 0 && (
            <p className="p-8 text-muted-foreground text-center">No projects yet. Add one or restart the backend to seed defaults.</p>
          )}
          {projects.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-4 p-4 border-b border-border last:border-0 hover:bg-secondary/40 transition-colors"
            >
              <img src={p.img || p.image_url} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{p.title}</p>
                <p className="text-sm text-muted-foreground">
                  {p.cat}
                  {p.area ? ` · ${p.area}` : ""} · order {p.sort_order}
                </p>
              </div>
              <span
                className={`text-xs px-2.5 py-1 rounded-full ${
                  p.published !== false ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                }`}
              >
                {p.published !== false ? "Live" : "Hidden"}
              </span>
              <button type="button" onClick={() => openEdit(p)} className="grid place-items-center w-9 h-9 rounded-full border border-border hover:border-accent">
                <Pencil size={16} />
              </button>
              <button type="button" onClick={() => remove(p.id)} className="grid place-items-center w-9 h-9 rounded-full border border-border hover:border-destructive hover:text-destructive">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setShowForm(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={save}
            className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-card border border-border p-8 space-y-4"
          >
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-serif-display text-2xl">{editingId ? "Edit Project" : "New Project"}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="grid place-items-center w-9 h-9 rounded-full border border-border">
                <X size={18} />
              </button>
            </div>
            <AdminField label="Title">
              <input className={inputCls} value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))} required />
            </AdminField>
            <AdminField label="Category">
              <select className={inputCls} value={form.cat} onChange={(e) => setForm((s) => ({ ...s, cat: e.target.value }))}>
                {CATS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </AdminField>
            <AdminField label="Area (optional, e.g. 2,400 sq.ft)">
              <input className={inputCls} value={form.area} onChange={(e) => setForm((s) => ({ ...s, area: e.target.value }))} />
            </AdminField>
            <AdminField label="Image URL">
              <input className={inputCls} value={form.image_url} onChange={(e) => setForm((s) => ({ ...s, image_url: e.target.value }))} required />
            </AdminField>
            <AdminField label="Grid width (Projects page)">
              <select className={inputCls} value={form.span} onChange={(e) => setForm((s) => ({ ...s, span: e.target.value }))}>
                {SPANS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </AdminField>
            <AdminField label="Sort order">
              <input type="number" className={inputCls} value={form.sort_order} onChange={(e) => setForm((s) => ({ ...s, sort_order: e.target.value }))} />
            </AdminField>
            <label className="flex items-center gap-3 text-sm">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm((s) => ({ ...s, published: e.target.checked }))} />
              Published on website
            </label>
            <button type="submit" disabled={saving} className="w-full rounded-full bg-primary text-primary-foreground py-3.5 font-medium disabled:opacity-50">
              {saving ? "Saving…" : "Save Project"}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
